import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Check, Send, Mail } from "lucide-react";
import PageShell from "@/components/PageShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Input = (props) => (
  <input
    {...props}
    className="w-full h-12 px-4 bg-[#0A0A0A] border border-white/10 rounded-2xl text-white placeholder:text-white/30 focus:outline-none focus:border-[#00FF88]/50 focus:ring-2 focus:ring-[#00FF88]/20 transition-all"
  />
);

const Contact = () => {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const valid =
    form.first_name.trim().length >= 1 &&
    /^\S+@\S+\.\S+$/.test(form.email) &&
    form.subject.trim().length >= 2 &&
    form.message.trim().length >= 2;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid || loading) return;
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        ...form,
        source_page: "/contact",
      });
      setSent(true);
      toast.success("Message sent. We'll be in touch.");
    } catch (err) {
      toast.error("Couldn't send", {
        description: err?.response?.data?.detail || "Please try again in a moment.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageShell
      eyebrow="// Get in touch"
      title="Contact Us"
      subtitle="Have questions, feedback, partnership opportunities, or media inquiries? We'd love to hear from you."
      testid="contact-page"
    >
      <a
        href="mailto:gigafloatapp@gmail.com"
        className="inline-flex items-center gap-2 mb-10 text-[#00FF88] hover:text-white transition-colors font-mono-c text-sm"
        data-testid="contact-email-link"
      >
        <Mail className="w-4 h-4" />
        gigafloatapp@gmail.com
      </a>

      {sent ? (
        <div
          className="rounded-2xl border border-[#00FF88]/30 bg-[#00FF88]/8 p-8 text-center"
          data-testid="contact-success"
        >
          <div className="relative w-20 h-20 mx-auto mb-5">
            <div className="absolute inset-0 rounded-full bg-[#00FF88]/30 blur-2xl animate-pulse" />
            <div className="relative w-full h-full rounded-full bg-[#0A0A0A] border border-[#00FF88]/40 grid place-items-center">
              <Check className="w-8 h-8 text-[#00FF88]" />
            </div>
          </div>
          <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight mb-2">
            Thank you for reaching out.
          </h2>
          <p className="text-white/65">
            We'll get back to you as soon as possible.
          </p>
        </div>
      ) : (
        <form
          onSubmit={submit}
          className="space-y-4"
          data-testid="contact-form"
        >
          <div className="grid sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="block font-mono-c text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">
                First name
              </span>
              <Input
                value={form.first_name}
                onChange={set("first_name")}
                placeholder="Alex"
                required
                data-testid="contact-first-name"
              />
            </label>
            <label className="block">
              <span className="block font-mono-c text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">
                Last name
              </span>
              <Input
                value={form.last_name}
                onChange={set("last_name")}
                placeholder="Morgan"
                data-testid="contact-last-name"
              />
            </label>
          </div>

          <label className="block">
            <span className="block font-mono-c text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">
              Email address
            </span>
            <Input
              type="email"
              value={form.email}
              onChange={set("email")}
              placeholder="you@example.com"
              required
              data-testid="contact-email"
            />
          </label>

          <label className="block">
            <span className="block font-mono-c text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">
              Subject
            </span>
            <Input
              value={form.subject}
              onChange={set("subject")}
              placeholder="What's this about?"
              required
              data-testid="contact-subject"
            />
          </label>

          <label className="block">
            <span className="block font-mono-c text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">
              Message
            </span>
            <textarea
              value={form.message}
              onChange={set("message")}
              placeholder="Tell us a bit more..."
              rows={6}
              required
              maxLength={4000}
              className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 rounded-2xl text-white placeholder:text-white/30 focus:outline-none focus:border-[#00FF88]/50 focus:ring-2 focus:ring-[#00FF88]/20 transition-all resize-y"
              data-testid="contact-message"
            />
            <div className="flex justify-end mt-1">
              <span className="text-[10px] font-mono-c text-white/35">
                {form.message.length}/4000
              </span>
            </div>
          </label>

          <button
            type="submit"
            disabled={!valid || loading}
            className="inline-flex items-center gap-2 bg-[#00FF88] text-black font-bold text-sm px-6 py-3 rounded-full btn-glow disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="contact-submit"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                Send Message <Send className="w-4 h-4" />
              </>
            )}
          </button>
        </form>
      )}
    </PageShell>
  );
};

export default Contact;
